import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, CalendarClock } from 'lucide-react';
import { Routes } from '#Routes';
import { PreventionShieldCard } from '#components/dashboard/PreventionShieldCard';
import { BloodPressureCard } from '#components/dashboard/BloodPressureCard';
import { HealthProfileCard } from '#components/dashboard/HealthProfileCard';
import { QuickActionsCard } from '#components/dashboard/QuickActionsCard';
import { LabStatusCard } from '#components/dashboard/LabStatusCard';
import { HealthInterviewCard } from '#components/dashboard/HealthInterviewCard';
import { AttentionCenterCard } from '#components/dashboard/AttentionCenterCard';
import { useTranslation } from 'react-i18next';
import { useFetchUpcomingEventsQuery } from '../store/apis/calendarAPI';
import moment from 'moment';

const dummyKeyTrackers = [
  { id: 1, name: 'Glucose', value: '95 mg/dL', trend: 'stable', graphData: [90, 92, 95, 93, 95] },
  { id: 2, name: 'eGFR', value: '62 mL/min', trend: 'down', graphData: [68, 65, 66, 63, 62] },
  {
    id: 3,
    name: 'Cholesterol',
    value: '180 mg/dL',
    trend: 'up',
    graphData: [170, 175, 172, 178, 180],
  },
];

const dummyRecentActivity = [
  {
    id: 1,
    text: 'AI Health Report (Kidney Function) Generated',
    date: 'Apr 7, 2025',
    type: 'report',
  },
  { id: 2, text: 'Lab Document (Urinalysis Q2) Uploaded', date: 'Apr 6, 2025', type: 'document' },
  { id: 3, text: 'Symptom Log: Mild fatigue noted', date: 'Apr 5, 2025', type: 'log' },
];

const TopBar = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const language = searchParams.get('language');
    if (language && ['en', 'pl'].includes(language)) {
      i18n.changeLanguage(language);
    }
  }, [searchParams, i18n]);

  const today = new Date().toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Row className='mb-4'>
      <Col>
        <Card border='0' className='shadow'>
          <Card.Body className='border-bottom px-4 py-3'>
            <h1 className='text-balance mb-2 text-xl font-bold tracking-tight text-foreground md:text-2xl'>
              {t('dashboard.page-heading')}
            </h1>
            <p className='mb-0 d-flex align-items-center gap-2 text-sm text-muted-foreground'>
              <Calendar className='flex-shrink-0' size={16} strokeWidth={2} aria-hidden />
              <span className='text-capitalize'>{today}</span>
            </p>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

const HealthLogCard = ({ onLogMood, onLogSymptoms }) => {
  const { t } = useTranslation();
  const [lastLog, setLastLog] = useState('Feeling good, mild headache');

  const handleMoodLog = (mood) => {
    onLogMood(mood);
    setLastLog(`Logged mood: ${mood}`);
  };

  return (
    <Card border='0' className='shadow h-100'>
      <Card.Body className='d-flex flex-column'>
        <h2 className='h5 mb-3'>{t('dashboard.todays-health-log')}</h2>
        <p className='mb-3'>{t('dashboard.how-are-you-feeling')}</p>
        <div className='d-flex gap-2 mb-3'>
          <Button variant='outline-secondary' onClick={() => handleMoodLog('😊')}>
            😊
          </Button>
          <Button variant='outline-secondary' onClick={() => handleMoodLog('😐')}>
            😐
          </Button>
          <Button variant='outline-secondary' onClick={() => handleMoodLog('😟')}>
            😟
          </Button>
        </div>
        <Button variant='success' className='mb-3' onClick={onLogSymptoms}>
          <span className='me-2'>[LOG]</span> {t('dashboard.log-detailed-symptoms')}
        </Button>
        <p className='text-muted small mb-0'>
          {t('dashboard.last-entry')}: {lastLog}
        </p>
      </Card.Body>
    </Card>
  );
};

const KeyTrackersCard = ({ trackers, onConfigure }) => {
  const { t } = useTranslation();
  return (
    <Card border='0' className='shadow h-100'>
      <Card.Body className='d-flex flex-column'>
        <h2 className='h5 mb-3'>{t('dashboard.key-lab-trackers')}</h2>
        <div className='d-flex flex-column gap-3'>
          {trackers.map((tracker) => (
            <div key={tracker.id} className='border-bottom pb-2'>
              <div className='d-flex align-items-center'>
                <span className='me-2'>{tracker.name}:</span>
                <span className='fw-bold'>{tracker.value}</span>
                <span className='ms-2'>
                  {tracker.trend === 'up' ? '↑' : tracker.trend === 'down' ? '↓' : '→'}
                </span>
              </div>
              <div className='bg-light rounded mt-2 p-2 text-center small text-muted'>
                {t('dashboard.graph-for')} {tracker.name}
              </div>
            </div>
          ))}
        </div>
        <Button
          variant='link'
          className='text-decoration-none align-self-end mt-3'
          onClick={onConfigure}
        >
          {t('dashboard.configure-trackers')}
          &gt;
        </Button>
      </Card.Body>
    </Card>
  );
};

const RemindersCard = ({ onViewAll }) => {
  const { t, i18n } = useTranslation();
  const { data: upcomingEvents, isLoading, error } = useFetchUpcomingEventsQuery();
  const navigate = useNavigate();

  const formatEventDate = (dateString) =>
    moment(dateString).format(i18n.language === 'pl' ? 'DD.MM.YYYY' : 'MMM D, YYYY');

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };

  return (
    <Card className='reminders-bootstrap-card border-0 shadow h-100 d-flex flex-column'>
      <Card.Header className='bg-white border-bottom py-3 pb-2'>
        <div
          className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
          style={{ fontSize: '1rem' }}
        >
          <CalendarClock
            className='flex-shrink-0'
            style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary)' }}
            strokeWidth={2}
            aria-hidden
          />
          {t('dashboard.upcoming-reminders')}
        </div>
      </Card.Header>
      <Card.Body className='pt-3 d-flex flex-column flex-grow-1 min-h-0'>
        {isLoading ? (
          <div className='text-center py-4 flex-grow-1 d-flex align-items-center justify-content-center'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : error ? (
          <p className='text-muted mb-0'>{t('dashboard.error-loading-reminders')}</p>
        ) : upcomingEvents && upcomingEvents.length > 0 ? (
          <ul className='list-unstyled mb-3 reminders-list flex-grow-1'>
            {upcomingEvents.map((event) => (
              <li key={event.id} className='border-bottom py-3'>
                <div className='d-flex justify-content-between align-items-start gap-3'>
                  <div className='flex-grow-1 min-w-0'>
                    <div className='reminders-event-title mb-1'>{event.title}</div>
                    {event.description && (
                      <p
                        className='mb-0 small text-muted'
                        title={event.description}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {truncateText(event.description)}
                      </p>
                    )}
                  </div>
                  <div className='flex-shrink-0 text-end'>
                    <div className='reminders-event-date'>{formatEventDate(event.start_time)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className='text-center py-4 flex-grow-1 d-flex flex-column justify-content-center'>
            <p className='text-muted mb-2 small'>{t('dashboard.no-data-available')}</p>
            <Button variant='logo-green' size='sm' onClick={() => navigate('/calendar')}>
              {t('dashboard.add-first-event')}
            </Button>
          </div>
        )}
        <Button
          variant='link'
          className='text-decoration-none align-self-end mt-auto small p-0'
          onClick={onViewAll}
        >
          {t('dashboard.view-all-reminders')} &gt;
        </Button>
      </Card.Body>
    </Card>
  );
};

const RecentActivityCard = ({ activities, onViewAll }) => {
  const { t } = useTranslation();
  return (
    <Card border='0' className='shadow h-100'>
      <Card.Body className='d-flex flex-column'>
        <h2 className='h5 mb-3'>{t('dashboard.recent-activity')}</h2>
        {activities.length > 0 ? (
          <ul className='list-unstyled mb-3'>
            {activities.map((activity) => (
              <li key={activity.id} className='border-bottom py-2'>
                <span className='fw-bold'>[{activity.type.toUpperCase()}] </span>
                {activity.text} - <em className='text-muted small'>{activity.date}</em>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted mb-3'>{t('dashboard.no-recent-activity')}</p>
        )}
        <Button
          variant='link'
          className='text-decoration-none align-self-end mt-auto'
          onClick={onViewAll}
        >
          {t('dashboard.view-all-activity')} &gt;
        </Button>
      </Card.Body>
    </Card>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const handleLogMood = (mood) => alert(`Mood logged: ${mood}`);
  const handleLogSymptoms = () => alert('Log Detailed Symptoms clicked!');
  const handleViewAllReminders = (e) => {
    e.preventDefault();
    navigate(Routes.Calendar.path); // Navigate to calendar page
  };
  const handleViewAllActivity = (e) => {
    e.preventDefault();
    navigate(Routes.HealthReport.path);
  };

  return (
    <div className='p-4 bg-light min-vh-100'>
      <TopBar />
      <Row className='g-4 mb-4 align-items-stretch'>
        <Col xs={12} lg={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <PreventionShieldCard />
          </div>
        </Col>
        <Col xs={12} lg={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <BloodPressureCard />
          </div>
        </Col>
        <Col xs={12} lg={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <AttentionCenterCard />
          </div>
        </Col>
      </Row>
      <Row className='g-4 mb-4 align-items-stretch'>
        <Col xs={12} lg={8} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <HealthProfileCard />
          </div>
        </Col>
        <Col xs={12} lg={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <QuickActionsCard />
          </div>
        </Col>
      </Row>
      <Row className='g-4 align-items-stretch'>
        <Col xs={12} md={6} xxl={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <RemindersCard onViewAll={handleViewAllReminders} />
          </div>
        </Col>
        <Col xs={12} md={6} xxl={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <LabStatusCard />
          </div>
        </Col>
        <Col xs={12} md={6} xxl={4} className='d-flex flex-column'>
          <div className='dashboard-bootstrap-tile flex-grow-1 d-flex flex-column min-h-0 w-100'>
            <HealthInterviewCard />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
